// Story 1.27 — TanStack Query hook for GET /api/v1/admin/creator-applications/:id.
//
// Backs the AdminApplicationDetailPage side-panel view. Carries the
// signed image URL (30-min TTL) + the full linked_accounts JSONB + the
// applicant's email/email-verified/account-created metadata + the
// computed preview_slug (the slug shape the admin can expect at
// approval time, modulo collision resolution).

import { useQuery } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type AdminLinkedAccount = {
  kind: string;
  url: string;
  verified_at?: string;
};

export type AdminApplicationDetail = {
  application_id: string;
  user_id: string;
  display_name: string;
  hello_text: string;
  image_signed_url: string | null;
  image_signed_url_expires_at: string | null;
  signature_signed_url: string | null;
  linked_accounts: AdminLinkedAccount[];
  region: string;
  language: string;
  status: "pending" | "approved" | "rejected" | "withdrawn";
  submitted_at: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
  rejection_reason: string | null;
  applicant_email: string;
  applicant_email_verified: boolean;
  applicant_account_created_at: string;
  applicant_existing_handle: string | null;
  preview_slug: string | null;
  /** Story 8.9 — the identity block declared at application time.
   *
   *  All null TOGETHER for one of TWO reasons, and the panel must not
   *  conflate them: the application predates migration 0094, or (Story
   *  8.11) the identity was erased when the applicant deleted their
   *  account. `applicant_account_erased` below is which.
   *
   *  There is no `tin` field here, deliberately: the column is dormant and
   *  the server does not select it. */
  legal_name: string | null;
  address_line1: string | null;
  address_line2: string | null;
  postal_code: string | null;
  city: string | null;
  country_code: string | null;
  business_status: string | null;
  business_registration_number: string | null;
  /** Story 8.11 — the applicant anonymised their account, so a null
   *  identity block above means "erased with the account", not "we never
   *  asked". Server-derived from the `@anonymized.greenhold` email
   *  sentinel. */
  applicant_account_erased: boolean;
  /** Prior portfolio consent withdrawals from an earlier creator stint
   *  — a re-applicant who left the program before. Newest first. */
  prior_consent_withdrawals: {
    portfolio_id: string;
    portfolio_title: string;
    withdrawn_at: string;
  }[];
  /** Story 1.38 + 7.20 — the applicant's active, OAuth-proven channel
   *  ownership claims, one per provider. Advisory (verification isn't
   *  required to apply); a strong positive signal when present. Empty
   *  when the applicant never verified anything.
   *
   *  WAS a single `twitch_identity_claim`. That shape could not show a
   *  YouTube proof once one existed, and a reviewer reading "no
   *  ownership proven" about an applicant who HAD proven ownership is
   *  worse than showing nothing. */
  identity_claims: {
    provider: string;
    login: string;
    provider_account_id: string;
    display_name: string | null;
    method: string;
    verified_at: string;
  }[];
};

export type AdminApplicationDetailEnvelope = { data: AdminApplicationDetail };

export type AdminApplicationDetailError = {
  code: string;
  message: string;
  status: number;
};

async function fetchAdminApplicationDetail(
  applicationId: string
): Promise<AdminApplicationDetailEnvelope> {
  const response = await fetch(
    `/api/v1/admin/creator-applications/${applicationId}`,
    { credentials: "include" }
  );
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: AdminApplicationDetailError = {
      code: "ADMIN_APP_DETAIL_FETCH_FAILED",
      message: `Detail fetch failed (HTTP ${response.status})`,
      status: response.status,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = {
          code: raw.error.code,
          message: raw.error.message,
          status: response.status,
        };
      }
    } catch {
      // body not JSON
    }
    throw detail;
  }
  return (await response.json()) as AdminApplicationDetailEnvelope;
}

export function useAdminCreatorApplicationDetail(applicationId: string) {
  return useQuery<AdminApplicationDetailEnvelope, AdminApplicationDetailError>({
    queryKey: ["admin-creator-application-detail", applicationId],
    queryFn: () => fetchAdminApplicationDetail(applicationId),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: false,
  });
}
